import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { usePathname } from 'expo-router';

import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { ROUTES } from '@/constants/routes';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useCalloutBanner } from '@/hooks/useCalloutBanner';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { CALLOUT_KEYS } from '@/services/userCallouts';
import { isEHRSession } from '@/util/smartLaunch';

/**
 * Whether the Fellows Corner promo is on screen, plus its dismiss action.
 *
 * Read by two callers with opposite intent: the promo itself, to render, and the
 * native announcement gate, to stay out of its way. Both must agree exactly, or
 * a frame shows either two banners or none — hence one hook rather than a
 * condition repeated per call site. On web the two banners stack instead, so the
 * slot there reads this only to decide whether the promo is one of the cards.
 *
 * Visibility is the render site (web only, from the secure layout), the feature
 * flag, an unresolved callout, not needing NPI verification (the secure layout
 * itself renders during that pending state, and a promo is not a reason to leave
 * it), and not being on the destination page, where the call to action would
 * only re-navigate. Dismissal resolves the callout server-side, so the promo
 * stays gone across the reader's devices.
 */
export function useFellowMentorBanner() {
  const pathname = usePathname();

  const isFellowsCornerEnabled = useFeatureFlag(FEATURE_FLAGS.FELLOWS_CORNER);

  const { needsNpiVerification } = useAuthStatus();

  const { isVisible, dismiss } = useCalloutBanner({
    key: CALLOUT_KEYS.FELLOWS_CORNER_INTRO,
    enabled: isFellowsCornerEnabled && !needsNpiVerification,
  });

  const isShowing = isWeb && isVisible && pathname !== ROUTES.FELLOWS && !isEHRSession();

  return { isShowing, dismiss };
}
